<template>
    <div>
        <ContentRiskActions
            v-if="unlayerEmailNewAutomationFlowEnabled"
            v-slot="contentRiskProps"
        >
            <AutomationEmailEditorPage
                v-bind="{...$props, ...contentRiskProps}"
                @closeCampaignUnlayerEmailBuilderModal="$emit('closeCampaignUnlayerEmailBuilderModal')"
                @campaignUnlayerEmailReady="$emit('campaignUnlayerEmailReady')"
                @campaignUnlayerEmailNotReady="$emit('campaignUnlayerEmailNotReady')"
            />
        </ContentRiskActions>

        <DsModal
            v-else
            is-open
            size="fullscreen"
            :show-back="stageIndex > 0"
            :show-close="stageIndex === 0"
            :intercept-close="stageIndex > 0"
            sticky-desktop-header
            :title="title"
            @close="handleClose"
            @back="handleBack"
        >
            <template #header>
                <div class="stage-indicator-container">
                    <StageIndicator
                        :active-stage="stageIndex"
                        :stages="stages"
                        @go-to-stage="goToStage"
                    />
                </div>

                <portal-target name="unlayer-email-editor-actions" />
            </template>

            <template #default>
                <section class="stages-container">
                    <ContentRiskActions v-slot="contentRiskProps">
                        <div v-if="loading" class="spinner-container">
                            <DsSpinner />
                        </div>

                        <TemplateSelectionStage
                            v-else-if="stageIndex === $options.STAGE_INDEXES.TEMPLATE_SELECTION"
                            :marketing-email-id="marketingEmailId"
                            :preview-enabled="campaignEmailPreviewEnabled"
                            :preview-logo-branding-center-enabled="previewLogoBrandingCenterEnabled"
                            :filter-my-templates-enabled="filterMyTemplatesEnabled"
                            @template-created="handleTemplateCreated"
                            @template-selected="handleTemplateSelected"
                        />

                        <UnlayerEmailEditStage
                            v-else-if="stageIndex === $options.STAGE_INDEXES.EDIT_TEMPLATE"
                            v-bind="contentRiskProps"
                            :email-template="emailTemplate"
                            :app-id="appId"
                            :environment="environment"
                            :unlayer-project-id="unlayerProjectId"
                            :merge-fields="mergeFields"
                            :campaign-links="campaignLinks"
                            :users="users"
                            :user-signatures="userSignatures"
                            :booking-link-hostname="bookingLinkHostname"
                            :appointment-types="appointmentTypes"
                            :custom-tools-uri="customToolsUri"
                            :tags="tags"
                            :i18n-enabled="i18nEnabled"
                            :unlayer-appointment-tool-enabled="unlayerAppointmentToolEnabled"
                            :unlayer-ai-tools-enabled="unlayerAiToolsEnabled"
                            :unlayer-smart-merge-tags-enabled="unlayerSmartMergeTagsEnabled"
                            :unlayer-email-audit-tool-enabled="unlayerEmailAuditToolEnabled"
                            :unlayer-builder-version-override="unlayerBuilderVersionOverride"
                            :use-unlayer-builder-version-override-enabled="useUnlayerBuilderVersionOverrideEnabled"
                            :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                            :image-search-unlayer-email-enabled="imageSearchUnlayerEmailEnabled"
                            :unlayer-email-logo-tool-enabled="unlayerEmailLogoToolEnabled"
                            :from-selection-enabled="fromSelectionEnabled"
                            :send-test-emails-to-non-users-enabled="sendTestEmailsToNonUsersEnabled"
                            :add-email-test-instructions-enabled="addEmailTestInstructionsEnabled"
                            :funnel-id="funnelId"
                            @update-email-template="handleUpdateEmailTemplate"
                            @template-edit-complete="handleTemplateEditComplete"
                        />

                        <ReadyStage
                            v-else-if="stageIndex === $options.STAGE_INDEXES.READY"
                            v-bind="contentRiskProps"
                            :environment="environment"
                            :email-template="emailTemplate"
                            :merge-fields="mergeFields"
                            :ready.sync="readyCellValue"
                            :users="users"
                            :funnel-id="funnelId"
                            :content-risk-failed="contentRiskCheckFailed"
                            :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                            @template-ready="handleClose"
                            @update:ready="handleReady"
                        />
                    </ContentRiskActions>
                </section>
            </template>
        </DsModal>

        <portal-target name="unlayer-email-editor-root" />
    </div>
</template>

<script>
import axios from 'axios';
import StageIndicator from '@/components/broadcast/StageIndicator.vue';
import AutomationEmailEditorPage from '@/components/unlayer/AutomationEmailEditorPage.vue';
import TemplateSelectionStage from '@/components/unlayer/TemplateSelectionStage.vue';
import UnlayerEmailEditStage from '@/components/unlayer/UnlayerEmailEditStage.vue';
import ReadyStage from '@/components/unlayer/ReadyStage.vue';
import ContentRiskActions from '@/components/unlayer/ContentRiskActions.vue';
import { STAGE_STATUS } from '@/components/broadcast/constants/emailBroadcast.constants';
import {
    getMergeFields,
    getCampaignLinks,
    getUsers,
    getUserSignatures,
    getAppointmentTypes,
    getTags,
} from './campaignUnlayerEmail';
import { isSpaApp } from '@/api/shared';
import {
    convertCampaignLinkIdsToString,
    replaceCampaignLinkIdsWithKeys,
    replaceCampaignMergeFieldIdsWithKeys,
    mergeCategoryAndTagName,
} from '@/components/unlayer/unlayerEmail.util';
import sentry from '@/analytics/sentry';

const STAGE_INDEXES = {
    TEMPLATE_SELECTION: 0,
    EDIT_TEMPLATE: 1,
    READY: 2,
};

export default {
    STAGE_INDEXES,

    components: {
        ContentRiskActions,
        ReadyStage,
        StageIndicator,
        TemplateSelectionStage,
        UnlayerEmailEditStage,
        AutomationEmailEditorPage,
    },


    props: {
        marketingEmailId: {
            type: String,
            default: '',
        },
        funnelId: {
            type: String,
            default: '',
        },
        title: {
            type: String,
            default: '',
        },
        unlayerProjectId: {
            type: String,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
        environment: {
            type: String,
            required: true,
        },
        bookingLinkHostname: {
            type: String,
            default: '',
        },
        customToolsUri: {
            type: String,
            default: '',
        },
        campaignEmailPreviewEnabled: Boolean,
        filterMyTemplatesEnabled: Boolean,
        i18nEnabled: Boolean,
        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
        fromSelectionEnabled: Boolean,
        unlayerBuilderVersionOverride: {
            type: String,
            default: '',
        },
        unlayerEmailAuditToolEnabled: Boolean,
        unlayerAiToolsEnabled: Boolean,
        unlayerSmartMergeTagsEnabled: Boolean,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
        sendTestEmailsToNonUsersEnabled: Boolean,
        addEmailTestInstructionsEnabled: Boolean,
        wrapSenderDisplayNameWithQuotesEnabled: Boolean,
        isReady: Boolean,
        isKonva: Boolean,
        imageSearchUnlayerEmailEnabled: Boolean,
        previewLogoBrandingCenterEnabled: Boolean,
        unlayerEmailNewAutomationFlowEnabled: Boolean,
        unlayerEmailLogoToolEnabled: Boolean,
        replyToEnabled: Boolean,
        unlayerFileDownloadHyperlinksEnabled: Boolean,
    },

    emits: ['closeCampaignUnlayerEmailBuilderModal', 'campaignUnlayerEmailReady', 'campaignUnlayerEmailNotReady'],

    data() {
        return {
            stageIndex: 0,
            emailTemplate: null,
            mergeFields: [],
            campaignLinks: [],
            users: [],
            userSignatures: {},
            appointmentTypes: [],
            tags: [],
            loadingEmailTemplate: true,
            loadingMergeFields: true,
            loadingCampaignLinks: true,
            loadingUsers: true,
            loadingUserSignatures: true,
            loadingAppointmentTypes: true,
            loadingTags: true,
            loadingUnlayerOptions: true,
            contentRiskCheckFailed: false,
            stagesMap: [
                { labelKey: '' },
                { labelKey: '' },
                { labelKey: '' },
            ],
            unlayerAppointmentToolEnabled: false,
            readyCellValue: false,
        };
    },

    computed: {
        loading() {
            return this.loadingEmailTemplate
                || this.loadingMergeFields
                || this.loadingCampaignLinks
                || this.loadingUsers
                || this.loadingUserSignatures
                || this.loadingAppointmentTypes
                || this.loadingTags
                || this.loadingUnlayerOptions;
        },

        stages() {
            const { IN_PROGRESS, COMPLETE } = STAGE_STATUS;

            return this.stagesMap.map((stage, index) => {
                const isComplete = index < this.stageIndex;

                return {
                    label: stage.labelKey,
                    status: isComplete ? COMPLETE : IN_PROGRESS,
                    disabled: !isComplete,
                };
            });
        },
    },

    async mounted() {
        this.readyCellValue = this.isReady;
    },

    async created() {
        await Promise.allSettled([
            this.fetchEmailTemplate(),
            this.fetchMergeFields(),
            this.fetchCampaignLinks(),
            this.fetchUsers(),
            this.fetchUserSignatures(),
            this.fetchAppointmentTypes(),
            this.fetchTags(),
            this.fetchUnlayerOptions(),
        ]);

        this.stagesMap[0].labelKey = this.$t('unlayerBuilder.stages.template');
        this.stagesMap[1].labelKey = this.$t('unlayerBuilder.stages.create');
        this.stagesMap[2].labelKey = this.$t('unlayerBuilder.stages.ready');
    },

    methods: {
        goToStage(index) {
            if (index >= this.stageIndex) {
                return;
            }

            this.stageIndex = index;
        },

        handleBack() {
            if (this.stageIndex > STAGE_INDEXES.TEMPLATE_SELECTION) {
                this.stageIndex--;
            }
        },

        handleClose() {
            this.handleReady();

            this.$emit('closeCampaignUnlayerEmailBuilderModal');
        },

        handleReady() {
            const eventName = this.readyCellValue ? 'campaignUnlayerEmailReady' : 'campaignUnlayerEmailNotReady';

            this.$emit(eventName);
        },

        handleTemplateCreated({ design }) {
            // This design might contain a campaign link id stored as int
            convertCampaignLinkIdsToString(design);
            this.emailTemplate.design = design;
            this.stageIndex = STAGE_INDEXES.EDIT_TEMPLATE;
        },

        handleTemplateSelected(template) {
            this.emailTemplate.design = template.design;
            this.stageIndex = STAGE_INDEXES.EDIT_TEMPLATE;
        },

        handleUpdateEmailTemplate(t) {
            this.emailTemplate = {
                ...this.emailTemplate,
                ...t,
            };
        },

        handleTemplateEditComplete(contentRiskCheckFailed) {
            this.contentRiskCheckFailed = contentRiskCheckFailed;

            this.stageIndex = STAGE_INDEXES.READY;
        },

        async fetchEmailTemplate() {
            this.loadingEmailTemplate = true;

            try {
                const { data } = await axios.get(`/app/unlayerEmailFunnel/template/${this.marketingEmailId}?funnelId=${this.funnelId}`);
                let template = data.unlayerEmailTemplate;

                let design = template.design ? JSON.parse(template.design) : null;

                if (design != null && this.containsCampaignLinksWithId(template.htmlBody)) {
                    // check template for normal campaign link ids, migrate, save, reload.
                    const { data: { links } } = await axios.get(`/app/unlayerEmailFunnel/campaignLinks/${this.funnelId}`);

                    try {
                        replaceCampaignLinkIdsWithKeys(design, links);
                    } catch (e) {
                        this.emailTemplate = {
                            ...template,
                            design,
                        };

                        this.stageIndex = this.emailTemplate.design != null ? STAGE_INDEXES.EDIT_TEMPLATE : STAGE_INDEXES.TEMPLATE_SELECTION;
                        this.loadingEmailTemplate = false;

                        return;
                    }

                    const {
                        id,
                        htmlBody,
                        trackedLinks,
                        subject,
                        previewText,
                        toAddress,
                        fromAddress,
                    } = template;

                    await axios.put(`/app/unlayerEmailFunnel/template/${id}`, {
                        html: htmlBody,
                        design: JSON.stringify(design),
                        trackedLinks,
                        subject,
                        previewText,
                        toAddress,
                        fromAddress,
                        funnelId: this.funnelId,
                    });

                    const { data: { unlayerEmailTemplate } } = await axios.get(`/app/unlayerEmailFunnel/template/${this.marketingEmailId}?funnelId=${this.funnelId}`);

                    design = unlayerEmailTemplate.design ? JSON.parse(unlayerEmailTemplate.design) : null;
                } else {
                    // design obtained from the server might contain a campaign link id stored as int
                    convertCampaignLinkIdsToString(design);
                }

                if (design != null && this.containsCampaignMergeFieldsWithId(template.htmlBody)) {
                    // check template for normal campaign merge field ids if so -> migrate, save and then reload.
                    const { data: { funnelMergeFields } } = await axios.get(`/app/unlayerEmailFunnel/funnelMergeFields/${this.funnelId}`);

                    try {
                        const designString = JSON.stringify(design);
                        const replacedDesignString = replaceCampaignMergeFieldIdsWithKeys(designString, funnelMergeFields);
                        const replacedDesign = JSON.parse(replacedDesignString);
                        const replacedSubject = replaceCampaignMergeFieldIdsWithKeys(template.subject, funnelMergeFields);
                        const replacedPreviewText = replaceCampaignMergeFieldIdsWithKeys(template.previewText, funnelMergeFields);

                        const {
                            id,
                            htmlBody,
                            trackedLinks,
                            toAddress,
                            fromAddress,
                        } = template;

                        await axios.put(`/app/unlayerEmailFunnel/template/${id}`, {
                            html: htmlBody,
                            design: JSON.stringify(replacedDesign),
                            trackedLinks,
                            subject: replacedSubject,
                            previewText: replacedPreviewText,
                            toAddress,
                            fromAddress,
                            funnelId: this.funnelId,
                        });

                        const { data: { unlayerEmailTemplate } } = await axios.get(`/app/unlayerEmailFunnel/template/${this.marketingEmailId}?funnelId=${this.funnelId}`);

                        design = unlayerEmailTemplate.design ? JSON.parse(unlayerEmailTemplate.design) : null;
                        template = unlayerEmailTemplate;
                    } catch (e) {
                        this.emailTemplate = {
                            ...template,
                            design,
                        };

                        this.stageIndex = this.emailTemplate.design != null ? STAGE_INDEXES.EDIT_TEMPLATE : STAGE_INDEXES.TEMPLATE_SELECTION;
                        this.loadingEmailTemplate = false;

                        sentry.captureException(e);

                        return;
                    }
                }

                this.emailTemplate = {
                    ...template,
                    design,
                };

                this.stageIndex = this.emailTemplate.design != null
                    ? STAGE_INDEXES.EDIT_TEMPLATE
                    : STAGE_INDEXES.TEMPLATE_SELECTION;
            } catch (e) {
                this.$error({ message: 'Something went wrong loading your campaign email. Please try again later.' });
                sentry.captureException(e);
            }

            this.loadingEmailTemplate = false;
        },

        containsCampaignLinksWithId(html) {
            return html.includes('campaign-link-id') && !html.includes('campaign-link-id="K');
        },

        containsCampaignMergeFieldsWithId(html) {
            // eslint-disable-next-line
            const mergeKeyFound = html.match('Campaign\..*_K');
            // eslint-disable-next-line
            const mergeFieldFound = html.match('Campaign\..*_');

            return Boolean((mergeFieldFound !== null) && (mergeKeyFound === null));
        },

        async fetchMergeFields() {
            this.loadingMergeFields = true;

            try {
                this.mergeFields = await getMergeFields(this.funnelId);
            } catch (e) {
                sentry.captureException(e);
            } finally {
                this.loadingMergeFields = false;
            }
        },

        async fetchCampaignLinks() {
            this.loadingCampaignLinks = true;

            try {
                const data = await getCampaignLinks(this.funnelId);

                this.campaignLinks = data.links.map((link) => {
                    const idValue = link.linkKey;

                    return {
                        value: idValue,
                        label: link.name,
                    };
                });
            } catch (e) {
                this.$error({ message: this.$t('funnel.unlayerEmail.error.loadingCampaignLinks') });
                sentry.captureException(e);
            }

            this.loadingCampaignLinks = false;
        },

        async fetchUsers() {
            this.loadingUsers = true;

            try {
                this.users = await getUsers();
            } catch (e) {
                sentry.captureException(e);
            } finally {
                this.loadingUsers = false;
            }
        },

        async fetchUserSignatures() {
            this.loadingUserSignatures = true;

            try {
                this.userSignatures = await getUserSignatures();
            } catch (e) {
                sentry.captureException(e);
            } finally {
                this.loadingUserSignatures = false;
            }
        },

        async fetchAppointmentTypes() {
            this.loadingAppointmentTypes = true;

            try {
                this.appointmentTypes = await getAppointmentTypes();
            } catch (e) {
                this.appointmentTypes = [];
                sentry.captureException(e);
            } finally {
                this.loadingAppointmentTypes = false;
            }
        },

        async fetchTags() {
            this.loadingTags = true;

            try {
                const tags = await getTags();

                this.tags = mergeCategoryAndTagName(tags);
            } catch (e) {
                sentry.captureException(e);
            } finally {
                this.loadingTags = false;
            }
        },

        async fetchUnlayerOptions() {
            this.loadingUnlayerOptions = true;

            try {
                this.unlayerAppointmentToolEnabled = await isSpaApp(); // enabled for pro and max
            } catch (e) {
                sentry.captureException(e);
            } finally {
                this.loadingUnlayerOptions = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.stage-indicator-container {
    position: fixed;
    top: 0;
    left: 0;
    height: 1px;
    z-index: 10;
    width: calc(100% - #{$spacing-600});
    text-align: center;
}

.stages-container {
    height: calc(100vh - #{px-to-rem(56px)});
    margin: -$spacing-300;
    overflow: hidden;
}

.spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
}
</style>
